export type CartItem = {
  id: string;
  name: string;
  price: number;
  qty: number;
  imageUrl?: string;
  /** Size / color (etc.) shown as separate BEM chips — not baked into `name`. */
  options?: string[];
};

type CartState = {
  items: Record<string, CartItem>;
  /** Cached `Object.values(items)` — see refreshSnapshot for why this must be cached. */
  snapshot: CartItem[];
};

const stateByKey = new Map<string, CartState>();
const listenersByKey = new Map<string, Set<() => void>>();
/** Mini-cart open requests (add-to-cart → open drawer). */
const openListenersByKey = new Map<string, Set<() => void>>();
const STORAGE_PREFIX = 'platzona.cart.';

const getKey = (key: string) => key || 'default';

const storageKey = (key: string) => `${STORAGE_PREFIX}${getKey(key)}`;

function normalizeImageUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const url = raw.trim();
  return url || undefined;
}

function normalizeOptions(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const opts = raw
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  return opts.length ? opts : undefined;
}

function readPersisted(key: string): Record<string, CartItem> {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(storageKey(key));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, CartItem> = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const row = value as Partial<CartItem>;
      const name = typeof row.name === 'string' ? row.name : '';
      const price = typeof row.price === 'number' && Number.isFinite(row.price) ? row.price : 0;
      const qty = typeof row.qty === 'number' && Number.isFinite(row.qty) ? Math.max(0, Math.floor(row.qty)) : 0;
      const imageUrl = normalizeImageUrl(row.imageUrl);
      const options = normalizeOptions(row.options);
      if (!id || !name || qty <= 0) continue;
      const base: CartItem = { id, name, price, qty };
      if (imageUrl) base.imageUrl = imageUrl;
      if (options) base.options = options;
      out[id] = base;
    }
    return out;
  } catch {
    return {};
  }
}

function writePersisted(key: string, items: Record<string, CartItem>) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(storageKey(key), JSON.stringify(items));
  } catch {
    /* ignore quota / private mode */
  }
}

const ensure = (key: string): CartState => {
  const k = getKey(key);
  const existing = stateByKey.get(k);
  if (existing) return existing;
  const items = readPersisted(k);
  const init: CartState = { items, snapshot: Object.values(items) };
  stateByKey.set(k, init);
  return init;
};

/** `listItems()` is used as a `useSyncExternalStore` getSnapshot — it must return the
 * SAME array reference across calls when nothing changed, or React sees a "new" value
 * on every render (Object.values always allocates a fresh array) and re-renders
 * forever ("Maximum update depth exceeded"). Only rebuild the cached array here, from
 * inside a mutation, never from listItems() itself. */
const refreshSnapshot = (s: CartState) => {
  s.snapshot = Object.values(s.items);
};

const emit = (key: string) => {
  const k = getKey(key);
  const s = stateByKey.get(k);
  if (s) {
    refreshSnapshot(s);
    writePersisted(k, s.items);
  }
  const listeners = listenersByKey.get(k);
  if (!listeners) return;
  for (const l of listeners) l();
};

export const cartStore = {
  getSnapshot(key: string): CartState {
    return ensure(key);
  },
  subscribe(key: string, cb: () => void): () => void {
    const k = getKey(key);
    let set = listenersByKey.get(k);
    if (!set) {
      set = new Set();
      listenersByKey.set(k, set);
    }
    set.add(cb);
    return () => set?.delete(cb);
  },
  add(
    key: string,
    item: { id: string; name: string; price: number; imageUrl?: string; options?: string[] },
    qty = 1,
  ) {
    const s = ensure(key);
    const existing = s.items[item.id];
    const imageUrl = normalizeImageUrl(item.imageUrl);
    const options = normalizeOptions(item.options);
    if (existing) {
      existing.qty += qty;
      if (imageUrl && !existing.imageUrl) existing.imageUrl = imageUrl;
      if (options?.length && !existing.options?.length) existing.options = options;
    } else {
      const row: CartItem = { id: item.id, name: item.name, price: item.price, qty };
      if (imageUrl) row.imageUrl = imageUrl;
      if (options) row.options = options;
      s.items[item.id] = row;
    }
    emit(key);
  },
  remove(key: string, id: string) {
    const s = ensure(key);
    delete s.items[id];
    emit(key);
  },
  setQty(key: string, id: string, qty: number) {
    const s = ensure(key);
    const it = s.items[id];
    if (!it) return;
    if (qty <= 0) delete s.items[id];
    else it.qty = qty;
    emit(key);
  },
  clear(key: string) {
    const s = ensure(key);
    s.items = {};
    emit(key);
  },
  listItems(key: string): CartItem[] {
    return ensure(key).snapshot;
  },
  total(key: string): number {
    return cartStore.listItems(key).reduce((sum, item) => sum + item.price * item.qty, 0);
  },
  /** Sum of line quantities — for header badge. */
  count(key: string): number {
    return cartStore.listItems(key).reduce((sum, item) => sum + item.qty, 0);
  },
  /** Ask nav mini-cart to open (e.g. after add-to-cart). */
  requestOpen(key: string) {
    const k = getKey(key);
    const listeners = openListenersByKey.get(k);
    if (!listeners) return;
    for (const l of listeners) l();
  },
  subscribeOpen(key: string, cb: () => void): () => void {
    const k = getKey(key);
    let set = openListenersByKey.get(k);
    if (!set) {
      set = new Set();
      openListenersByKey.set(k, set);
    }
    set.add(cb);
    return () => set?.delete(cb);
  },
};

/** True when a nav label/URL looks like the storefront cart page. */
export function isCartNavLink(label: string, url: string): boolean {
  const l = (label || '').trim().toLowerCase();
  const u = (url || '').trim().toLowerCase();
  if (/^(warenkorb|cart|basket|bag|shopping\s*bag)$/i.test(l)) return true;
  if (/(warenkorb|cart|basket)/i.test(l) && l.length < 24) return true;
  return /\/(cart|warenkorb|basket)(\/|$|\?)/i.test(u);
}

export { isCheckoutNavLink } from './cartLineDisplay';
